import * as React from 'react';
import VerticalFormItem from "../form-items/VerticalBaseFormItem";
import {Contact, ErrorResponse} from "../../api/dtos/dtos";
import furoApi from "../../api/FuroApi";
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from "../../app/ApplicationContext";
import {ErrorAlert} from "../ErrorAlert";
import ClientContactsSelector from "./ClientContactsSelector";
import {WrappedFormUtils} from "antd/lib/form/Form";
import {showSaveSuccessNotification} from "../../utils/dialogs";
import {isNullOrBlank, isValidEmail} from "../../utils/string-utils";
import locales from "../../locales";

interface Props {
  fieldId: string;
  form: WrappedFormUtils;
  label?: React.ReactNode;
  disabled: boolean;
  required: boolean;
  clientId: number;
  initialContactIds: number[];
}

interface State {
  availableContacts: Contact[];
  selectedContactIds: number[];
  error?: ErrorResponse;
}

class ClientContactsFormItem extends React.Component<Props, State> {

  public readonly loadingHandler?: LoadingOverlayHandler;

  constructor(props: Props) {
    super(props);
    this.state = {
      availableContacts: [],
      selectedContactIds: props.initialContactIds,
    };
  }

  public componentDidMount() {
    this.loadClientContacts();
  }

  public render(): React.ReactNode {
    const {form, fieldId, label, initialContactIds, required, disabled} = this.props;
    const {availableContacts, selectedContactIds, error} = this.state;

        return (
            <>
                <ApplicationContextConsumer>
                    {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
                </ApplicationContextConsumer>

                <VerticalFormItem
                    fieldId={fieldId}
                    form={form}
                    label={label}
                    required={required}
                    initialValue={initialContactIds}
                    rules={[
                        {validator: this.validateContacts}
                    ]}
                    input={
                        <ClientContactsSelector
                            disabled={disabled}
                            contacts={availableContacts}
                            selectedContactIds={selectedContactIds}
                            onChange={this.onChange}
                            onSaveContact={this.saveContact}
                        />
                    }
                />

        {error && <ErrorAlert error={error} style={{marginTop: 16}}/>}
      </>
    );
  }

  private validateContacts = (_: any, contactIds: number[], callback: (msg?: string) => void) => {
    const {availableContacts} = this.state;

    const selectedContacts = availableContacts.filter((c) => contactIds.includes(c.id!));
    const hasContactWithNoEmail = selectedContacts.some((c) => isNullOrBlank(c.email));
    const hasContactWithInvalidEmail = selectedContacts.some((c) => c.email && !isValidEmail(c.email));
    if (hasContactWithNoEmail) {
      callback(locales.contact_selector_missing_email);
    } if (hasContactWithInvalidEmail) {
      callback(locales.contact_selector_invalid_email);
    } else {
      callback();
    }
  }


  private onChange = (contactIds: number[]) => {
    const {form, fieldId} = this.props;
    form.setFieldsValue({[fieldId]: contactIds});
    this.setState({selectedContactIds: contactIds});
  };

  private loadClientContacts = () => {
    const {clientId} = this.props;
    const promise = furoApi.clients.getContacts(clientId)
      .then(this.onClientContactsLoaded)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onClientContactsLoaded = (response: Contact[]) => {
    this.setState({availableContacts: response})
  };

  private saveContact = (contact: Contact) => {
    const clientId = this.props.clientId;
    const contactId = contact.id;
    if (contactId === null) {
      withLoadingOverlay(
        furoApi.clients.createContact(clientId, contact)
          .then(this.onSaveContactSuccess)
          .catch(this.onApiError), this.loadingHandler);
    } else {
      withLoadingOverlay(
        furoApi.clients.saveContact(clientId, contact, contactId)
          .then(this.onSaveContactSuccess)
          .catch(this.onApiError), this.loadingHandler);
    }
  };

  private onSaveContactSuccess = (contactId: number) => {
    const {selectedContactIds} = this.state;
    if (!selectedContactIds.includes(contactId)) {
      selectedContactIds.push(contactId);
    }
    this.onChange(selectedContactIds);

    this.loadClientContacts();
    showSaveSuccessNotification();
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  };
}

export default ClientContactsFormItem;


// WEBPACK FOOTER //
// ./src/common/contact/ClientContactsFormItem.tsx